import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/shared";

const sections = [
  { id: "responsable", n: "1", title: "Responsable du traitement" },
  { id: "donnees", n: "2", title: "Données susceptibles d'être collectées" },
  { id: "finalites", n: "3", title: "Finalités des traitements" },
  { id: "bases-legales", n: "4", title: "Bases légales" },
  { id: "destinataires", n: "5", title: "Destinataires" },
  { id: "conservation", n: "6", title: "Durées de conservation" },
  { id: "cookies", n: "7", title: "Cookies et traceurs" },
  { id: "securite", n: "8", title: "Sécurité" },
  { id: "droits", n: "9", title: "Vos droits" },
  { id: "desinscription", n: "10", title: "Désinscription" },
  { id: "transferts", n: "11", title: "Transferts et prestataires techniques" },
  { id: "evolution", n: "12", title: "Évolution de la politique" },
];

const H = ({ id, n, children }) => (
  <h2 id={id} className="scroll-mt-28 font-serif text-2xl text-bz-navy lg:text-3xl">
    <span className="mr-3 text-bz-amber">{n}.</span>
    {children}
  </h2>
);

const P = ({ children }) => <p className="mt-4 leading-relaxed text-bz-slate">{children}</p>;

const UL = ({ items }) => (
  <ul className="mt-4 space-y-2 text-bz-slate">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3 leading-relaxed">
        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-bz-amber" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const Mail = () => (
  <a href="mailto:consulting@benzenati.com" className="text-bz-petrol underline underline-offset-2">consulting@benzenati.com</a>
);

export default function Politique() {
  return (
    <>
      <Seo
        title="Politique de confidentialité — BENZENATI"
        description="Politique de confidentialité de l'Atelier BENZENATI : responsable du traitement, données collectées, finalités, bases légales, durées de conservation, cookies, sécurité et vos droits (RGPD)."
        path="/politique-de-confidentialite"
      />
      <PageHero label="Informations" title="Politique de confidentialité" testid="politique-hero" />

      <section className="mx-auto max-w-3xl px-6 py-16 lg:py-24" data-testid="politique-content">
        <p className="text-sm uppercase tracking-[0.15em] text-bz-slate/80" data-testid="politique-maj">
          Dernière mise à jour : 30 juillet 2026
        </p>

        <p className="mt-8 leading-relaxed text-bz-slate">
          BENZENATI accorde une attention particulière à la protection des données personnelles et s'engage à les traiter de manière transparente, proportionnée et sécurisée, conformément au règlement général sur la protection des données (RGPD) et à la loi française Informatique et Libertés.
        </p>

        {/* Table des matières */}
        <nav className="mt-10 border-y border-bz-line py-6" data-testid="politique-toc">
          <p className="bz-label text-bz-petrol">Sommaire</p>
          <ol className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-sm text-bz-slate transition-colors hover:text-bz-navy">
                  {s.n}. {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="mt-12 space-y-12">
          <div>
            <H id="responsable" n="1">Responsable du traitement</H>
            <P>Le responsable des traitements réalisés sur ce site est :</P>
            <div className="mt-4 border-l-2 border-bz-line pl-6 leading-relaxed text-bz-slate">
              BENZENATI<br />
              Entrepreneur individuel : Mohamed Benzenati<br />
              Activité de services aux entreprises<br />
              Bourgogne-Franche-Comté – France<br />
              Contact : <Mail />
            </div>
            <P>Les informations légales complètes de l'éditeur sont accessibles dans la page « Mentions légales ».</P>
          </div>

          <div>
            <H id="donnees" n="2">Données susceptibles d'être collectées</H>
            <P>Lors de l'inscription à La lettre BENZENATI, les données suivantes peuvent être recueillies :</P>
            <UL items={[
              "adresse électronique, obligatoire ;",
              "prénom, facultatif ;",
              "entreprise, facultatif ;",
              "fonction, facultative ;",
              "date et preuve du consentement ;",
              "statut de l'abonnement : en attente, actif ou désinscrit ;",
              "informations techniques strictement nécessaires à la sécurisation et à la gestion de l'inscription.",
            ]} />
            <P>Lorsque vous contactez BENZENATI par courrier électronique, les informations contenues dans votre message ainsi que vos coordonnées sont également traitées afin de répondre à votre demande.</P>
            <P>Le site peut enfin générer des journaux techniques limités, notamment l'adresse IP, la date de connexion, les pages demandées et les informations nécessaires à la détection d'une anomalie ou d'une tentative d'accès non autorisé.</P>
          </div>

          <div>
            <H id="finalites" n="3">Finalités des traitements</H>
            <P>Les données collectées sont utilisées uniquement pour :</P>
            <UL items={[
              "gérer les inscriptions et désinscriptions à La lettre BENZENATI ;",
              "adresser les décryptages et informations éditoriales demandés ;",
              "répondre aux messages reçus ;",
              "assurer le fonctionnement, la maintenance et la sécurité du site ;",
              "prévenir les inscriptions automatisées, abusives ou frauduleuses ;",
              "démontrer, lorsque cela est nécessaire, le recueil du consentement.",
            ]} />
            <P>Les données ne sont ni vendues, ni louées, ni utilisées pour constituer des profils publicitaires.</P>
          </div>

          <div>
            <H id="bases-legales" n="4">Bases légales</H>
            <P>L'envoi de La lettre BENZENATI repose sur le consentement de l'abonné. Ce consentement peut être retiré à tout moment.</P>
            <P>Le traitement des demandes reçues par courrier électronique repose sur l'intérêt légitime de BENZENATI à répondre aux personnes qui le contactent ou, lorsque cela est applicable, sur l'exécution de mesures demandées par la personne.</P>
            <P>Les opérations nécessaires à la sécurité du site reposent sur l'intérêt légitime de BENZENATI à protéger son système d'information, son administration et les données qu'il conserve.</P>
          </div>

          <div>
            <H id="destinataires" n="5">Destinataires</H>
            <P>Les données sont accessibles uniquement :</P>
            <UL items={[
              "à BENZENATI, dans la limite des besoins nécessaires ;",
              "aux prestataires techniques indispensables à l'hébergement, au stockage, à la maintenance et, lorsqu'il sera activé, à l'envoi de la newsletter ;",
              "aux autorités légalement habilitées, lorsqu'une obligation juridique l'impose.",
            ]} />
            <P>Ces prestataires n'ont pas l'autorisation d'utiliser les données pour leurs propres activités commerciales.</P>
          </div>

          <div>
            <H id="conservation" n="6">Durées de conservation</H>
            <P>Les informations liées à l'abonnement sont conservées pendant toute la durée de l'inscription.</P>
            <P>Lors d'une désinscription, l'adresse électronique n'est plus utilisée pour envoyer La lettre BENZENATI. Les données devenues inutiles sont supprimées ou anonymisées. Une preuve minimale de la demande et du retrait du consentement peut être conservée pendant une durée maximale de trois ans afin de démontrer le respect des obligations applicables et d'éviter une réinscription non souhaitée.</P>
            <P>Les messages reçus par courrier électronique sont conservés pendant la durée nécessaire au traitement de la demande, puis supprimés ou archivés selon leur utilité administrative ou les obligations légales applicables.</P>
            <P>Les journaux techniques de sécurité sont conservés pendant une durée limitée et proportionnée aux besoins de protection du site.</P>
          </div>

          <div>
            <H id="cookies" n="7">Cookies et traceurs</H>
            <P>Dans sa configuration actuelle, le site BENZENATI n'utilise pas de cookies publicitaires ni de dispositif de profilage commercial.</P>
            <P>Seuls des mécanismes techniques strictement nécessaires au fonctionnement, à la sécurité ou à l'administration du site peuvent être utilisés. Ils ne servent pas à suivre les visiteurs à des fins publicitaires.</P>
            <P>Si un outil de mesure d'audience, un contenu externe ou un traceur non indispensable est ajouté ultérieurement, la présente politique sera mise à jour et un dispositif de consentement sera installé lorsque la réglementation l'exige.</P>
          </div>

          <div>
            <H id="securite" n="8">Sécurité</H>
            <P>BENZENATI met en œuvre des mesures techniques et organisationnelles adaptées afin de protéger les données contre l'accès non autorisé, la perte, l'altération ou la divulgation.</P>
            <P>L'espace d'administration est protégé. Les mots de passe ne sont pas conservés en clair et les secrets techniques ne sont pas exposés dans le code public.</P>
            <P>Aucun système ne pouvant garantir une sécurité absolue, les mesures de protection sont régulièrement réexaminées en fonction de l'évolution du site et des risques.</P>
          </div>

          <div>
            <H id="droits" n="9">Vos droits</H>
            <P>Conformément à la réglementation, vous pouvez demander :</P>
            <UL items={[
              "l'accès à vos données ;",
              "leur rectification ;",
              "leur effacement ;",
              "la limitation de leur traitement ;",
              "leur portabilité lorsque ce droit est applicable ;",
              "votre opposition à certains traitements ;",
              "le retrait de votre consentement à tout moment.",
            ]} />
            <P>Vous pouvez exercer vos droits en écrivant à : <Mail />.</P>
            <P>La demande devra permettre de vous identifier de manière suffisante. Une pièce d'identité ne sera demandée qu'en cas de doute raisonnable sur l'identité du demandeur.</P>
            <P>
              Vous pouvez également introduire une réclamation auprès de la Commission nationale de l'informatique et des libertés :{" "}
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-bz-petrol underline underline-offset-2">www.cnil.fr</a>.
            </P>
          </div>

          <div>
            <H id="desinscription" n="10">Désinscription</H>
            <P>Chaque lettre envoyée comportera un moyen simple de se désinscrire. La demande de désinscription peut également être adressée à <Mail />.</P>
            <P>Le retrait du consentement n'affecte pas la licéité des traitements réalisés avant ce retrait.</P>
          </div>

          <div>
            <H id="transferts" n="11">Transferts et prestataires techniques</H>
            <P>BENZENATI fait appel à des prestataires techniques nécessaires à l'hébergement, au stockage, à la sécurisation et au fonctionnement du site.</P>
            <P>Certains prestataires peuvent être amenés à traiter des données depuis un pays situé en dehors de l'Espace économique européen. Lorsque cela se produit, BENZENATI veille à ce que le traitement repose sur un mécanisme reconnu par la réglementation applicable, notamment une décision d'adéquation ou des garanties contractuelles appropriées.</P>
            <P>Lorsqu'un prestataire d'envoi de courriels sera activé pour La lettre BENZENATI, les informations relatives à ce prestataire et aux conditions de traitement des données seront intégrées à la présente politique.</P>
            <P>Pour toute question relative aux prestataires utilisés ou aux garanties applicables, vous pouvez écrire à <Mail />.</P>
          </div>

          <div>
            <H id="evolution" n="12">Évolution de la politique</H>
            <P>Cette politique peut évoluer lorsque le site ajoute un nouveau formulaire, un outil de mesure d'audience, un service d'envoi de courriels, un agent automatisé ou une nouvelle fonctionnalité traitant des données personnelles.</P>
            <P>La date de dernière mise à jour est indiquée en haut de cette page.</P>
          </div>
        </div>
      </section>
    </>
  );
}
